/**
 * customEvents — pure helpers for the ENG-1062 named custom-event system.
 *
 * Split out of EventRuntime.js so they can be unit-tested: EventRuntime
 * transitively imports the asset/API layer (extensionless specifiers only Vite
 * resolves) and can't be imported under this repo's bare `node --test`. Same
 * reasoning as eventStarters.js.
 *
 * A `customEvent` node is a starter (exec-chain root) that is dispatched BY
 * NAME, not by type: many can share the type `customEvent`, each with its own
 * `data.name`. EventRuntime.fireCustomEvent(name) resolves the matching
 * starters through customEventStartersFor(); the transition editor lists
 * choosable names through customEventNames().
 */

/** Trim + string-coerce a node's event name. Empty when unnamed. */
function eventNameOf(node) {
  return String(node?.data?.name ?? '').trim()
}

/**
 * The customEvent starter nodes whose name matches `name`. Pass the already
 * by-type-filtered list (e.g. startersFor('customEvent', nodes)) so the caller
 * keeps the hot-path cache. An empty/blank `name` matches nothing — an unnamed
 * event can never be fired.
 * @param {string} name
 * @param {object[]} customEventNodes  nodes already filtered to type 'customEvent'
 * @returns {object[]}
 */
export function customEventStartersFor(name, customEventNodes) {
  const want = String(name ?? '').trim()
  if (!want) return []
  return (customEventNodes ?? []).filter(n => eventNameOf(n) === want)
}

/**
 * Sorted, de-duplicated list of non-empty custom-event names declared anywhere
 * in `nodes` (top level plus one level into any graphGroup container's
 * innerNodes — matching how EventRuntime discovers grouped starters). Drives
 * the transition editor's Actions picker.
 * @param {object[]} nodes  event-graph nodes
 * @returns {string[]}
 */
export function customEventNames(nodes) {
  const names = new Set()
  for (const n of nodes ?? []) {
    if (n?.type === 'customEvent') {
      const name = eventNameOf(n)
      if (name) names.add(name)
    }
    // one level into a graphGroup container's inner pool
    const inner = n?.data?.innerNodes
    if (Array.isArray(inner)) {
      for (const g of inner) {
        if (g?.type === 'customEvent') {
          const name = eventNameOf(g)
          if (name) names.add(name)
        }
      }
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b))
}
